/**
 * ClickableZone - An axis-aligned bounding box rendered as a 2D canvas overlay.
 *
 * The box is drawn by projecting its 8 corners to screen space each frame and
 * drawing the 12 edges with the Canvas 2D API. This is completely immune to
 * near-plane clipping, depth testing, and material rendering issues.
 *
 * Usage:
 *   events.invoke('clickableZones.add', {
 *       id: 'my-door',
 *       position: new Vec3(0, 1, 0),
 *       halfExtents: new Vec3(0.5, 1, 0.3),
 *       label: 'Open door',
 *   });
 *   events.on('clickableZone.clicked', (id: string) => { ... });
 *   events.on('clickableZone.hovered', (id: string | null) => { ... });
 *   events.fire('clickableZones.remove', 'my-door');
 */

import {
    BoundingBox,
    Color,
    Ray,
    Vec3,
} from 'playcanvas';

import { Events } from './events';
import { Scene } from './scene';

// ─── types ───────────────────────────────────────────────────────────────────

export interface ClickableZoneOptions {
    /** Stable identifier. Auto-generated UUID if omitted. */
    id?: string;
    /** World-space centre of the box. */
    position: Vec3;
    /** World-space half-extents. */
    halfExtents: Vec3;
    /** Render the box? Default true. */
    visible?: boolean;
    /** Tooltip string shown next to the cursor. */
    label?: string;
    /** Edge colour (default: cyan). */
    color?: Color;
    /** Edge colour when hovered (default: yellow). */
    hoverColor?: Color;
    /** Filenames of supporting images for this zone. */
    supportingImages?: string[];
}

// ─── internal zone record ────────────────────────────────────────────────────

interface ZoneRecord {
    id: string;
    options: Required<ClickableZoneOptions>;
    aabb: BoundingBox;
    hovered: boolean;
}

// ─── geometry helpers ─────────────────────────────────────────────────────────

let _uid = 0;
const uniqueId = () => `zone_${++_uid}`;

const _c = Array.from({ length: 8 }, () => new Vec3());
const _sc = Array.from({ length: 8 }, () => new Vec3());
const _clip = new Vec3();
const _s0 = new Vec3();
const _s1 = new Vec3();

function setCorners(pos: Vec3, he: Vec3) {
    _c[0].set(pos.x - he.x, pos.y - he.y, pos.z - he.z);
    _c[1].set(pos.x + he.x, pos.y - he.y, pos.z - he.z);
    _c[2].set(pos.x + he.x, pos.y + he.y, pos.z - he.z);
    _c[3].set(pos.x - he.x, pos.y + he.y, pos.z - he.z);
    _c[4].set(pos.x - he.x, pos.y - he.y, pos.z + he.z);
    _c[5].set(pos.x + he.x, pos.y - he.y, pos.z + he.z);
    _c[6].set(pos.x + he.x, pos.y + he.y, pos.z + he.z);
    _c[7].set(pos.x - he.x, pos.y + he.y, pos.z + he.z);
}

const EDGES: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [4, 5], [5, 6], [6, 7], [7, 4],
    [0, 4], [1, 5], [2, 6], [3, 7],
];

// ─── ray-AABB intersection (CPU, for hover) ───────────────────────────────────

function rayIntersectsAabb(ray: Ray, aabb: BoundingBox): boolean {
    const min = aabb.getMin(), max = aabb.getMax();
    let tMin = -Infinity, tMax = Infinity;
    const d = [ray.direction.x, ray.direction.y, ray.direction.z];
    const o = [ray.origin.x, ray.origin.y, ray.origin.z];
    const mn = [min.x, min.y, min.z], mx = [max.x, max.y, max.z];
    for (let i = 0; i < 3; i++) {
        if (Math.abs(d[i]) < 1e-8) {
            if (o[i] < mn[i] || o[i] > mx[i]) return false;
        } else {
            const inv = 1 / d[i];
            let t1 = (mn[i] - o[i]) * inv, t2 = (mx[i] - o[i]) * inv;
            if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
            tMin = Math.max(tMin, t1); tMax = Math.min(tMax, t2);
            if (tMin > tMax) return false;
        }
    }
    return tMax >= 0;
}

// ─── hover fill: convex hull of visible corners ───────────────────────────────

function drawHoverFill(
    ctx: CanvasRenderingContext2D,
    camPos: Vec3, camFwd: Vec3, near: number,
    worldToScreen: (w: Vec3, s: Vec3) => void
) {
    const pts: [number, number][] = [];
    for (let i = 0; i < 8; i++) {
        const p = _c[i];
        const d = (p.x - camPos.x) * camFwd.x + (p.y - camPos.y) * camFwd.y + (p.z - camPos.z) * camFwd.z - near;
        if (d >= 0) {
            worldToScreen(p, _sc[i]);
            pts.push([_sc[i].x, _sc[i].y]);
        }
    }
    if (pts.length < 3) return;
    const mx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const my = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    pts.sort((a, b) => Math.atan2(a[1] - my, a[0] - mx) - Math.atan2(b[1] - my, b[0] - mx));
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
    ctx.fill();
}

// ─── 2D edge drawing with near-plane clip ────────────────────────────────────

function strokeEdge(
    ctx: CanvasRenderingContext2D,
    p0: Vec3, p1: Vec3,
    camPos: Vec3, camFwd: Vec3, near: number,
    worldToScreen: (w: Vec3, s: Vec3) => void
) {
    const d0 = (p0.x - camPos.x) * camFwd.x + (p0.y - camPos.y) * camFwd.y + (p0.z - camPos.z) * camFwd.z - near;
    const d1 = (p1.x - camPos.x) * camFwd.x + (p1.y - camPos.y) * camFwd.y + (p1.z - camPos.z) * camFwd.z - near;

    if (d0 < 0 && d1 < 0) return;

    let a: Vec3 = p0, b: Vec3 = p1;
    if (d0 < 0 || d1 < 0) {
        const t = d0 / (d0 - d1);
        _clip.set(p0.x + t * (p1.x - p0.x), p0.y + t * (p1.y - p0.y), p0.z + t * (p1.z - p0.z));
        if (d0 < 0) a = _clip; else b = _clip;
    }

    worldToScreen(a, _s0);
    worldToScreen(b, _s1);
    ctx.moveTo(_s0.x, _s0.y);
    ctx.lineTo(_s1.x, _s1.y);
}

// ─── tooltip element ─────────────────────────────────────────────────────────

function createTooltipEl(): HTMLDivElement {
    const el = document.createElement('div');
    el.style.cssText = [
        'position:fixed', 'pointer-events:none', 'z-index:9999',
        'background:rgba(0,0,0,0.75)', 'color:#fff', 'padding:4px 8px',
        'border-radius:4px', 'font:13px/1.4 sans-serif',
        'white-space:nowrap', 'display:none', 'transform:translate(12px,-50%)'
    ].join(';');
    document.body.appendChild(el);
    return el;
}

// ─── main registration function ───────────────────────────────────────────────

export function registerClickableZoneEvents(events: Events, scene: Scene, canvas: HTMLElement) {
    const zones = new Map<string, ZoneRecord>();
    const ray = new Ray();
    let hoveredId: string | null = null;
    let mouseX = 0, mouseY = 0;

    // ── 2D overlay canvas ─────────────────────────────────────────────────────
    const overlay = document.createElement('canvas');
    overlay.style.cssText = 'position:fixed;pointer-events:none;z-index:10;';
    document.body.appendChild(overlay);
    const ctx2d = overlay.getContext('2d')!;

    function syncOverlay() {
        const r = canvas.getBoundingClientRect();
        overlay.style.left = `${r.left}px`;
        overlay.style.top = `${r.top}px`;
        overlay.style.width = `${r.width}px`;
        overlay.style.height = `${r.height}px`;
        overlay.width = Math.round(r.width);
        overlay.height = Math.round(r.height);
    }
    syncOverlay();
    window.addEventListener('resize', syncOverlay);

    // ── tooltip ───────────────────────────────────────────────────────────────
    const tooltipEl = createTooltipEl();

    const DEFAULT_COLOR       = new Color(0.0, 1.0, 1.0);
    const DEFAULT_HOVER_COLOR = new Color(1.0, 0.85, 0.0);

    // ── add zone ─────────────────────────────────────────────────────────────
    events.function('clickableZones.add', (opts: ClickableZoneOptions) => {
        const id = opts.id ?? uniqueId();
        if (zones.has(id)) {
            console.warn(`[ClickableZone] Zone '${id}' already exists. Remove it first.`);
            return id;
        }
        const fullOpts: Required<ClickableZoneOptions> = {
            id,
            position: opts.position.clone(),
            halfExtents: opts.halfExtents.clone(),
            visible: opts.visible !== false,
            label: opts.label ?? '',
            color: opts.color?.clone() ?? DEFAULT_COLOR.clone(),
            hoverColor: opts.hoverColor?.clone() ?? DEFAULT_HOVER_COLOR.clone(),
            supportingImages: opts.supportingImages ?? [],
        };
        const aabb = new BoundingBox(fullOpts.position.clone(), fullOpts.halfExtents.clone());
        zones.set(id, { id, options: fullOpts, aabb, hovered: false });
        scene.forceRender = true;
        return id;
    });

    // ── remove zone ───────────────────────────────────────────────────────────
    events.on('clickableZones.remove', (id: string) => {
        if (!zones.has(id)) return;
        zones.delete(id);
        if (hoveredId === id) {
            hoveredId = null;
            canvas.style.cursor = '';
            tooltipEl.style.display = 'none';
            events.fire('clickableZone.hovered', null);
        }
        scene.forceRender = true;
    });

    // ── clear all zones on scene reset ────────────────────────────────────────
    events.on('scene.clear', () => {
        zones.clear();
        hoveredId = null;
        canvas.style.cursor = '';
        tooltipEl.style.display = 'none';
        scene.forceRender = true;
    });

    // ── update zone ───────────────────────────────────────────────────────────
    events.on('clickableZones.update', (id: string, patch: Partial<ClickableZoneOptions>) => {
        const rec = zones.get(id);
        if (!rec) return;
        if (patch.position) { rec.options.position.copy(patch.position); rec.aabb.center.copy(patch.position); }
        if (patch.halfExtents) { rec.options.halfExtents.copy(patch.halfExtents); rec.aabb.halfExtents.copy(patch.halfExtents); }
        if (patch.visible !== undefined) rec.options.visible = patch.visible;
        if (patch.color) rec.options.color.copy(patch.color);
        if (patch.hoverColor) rec.options.hoverColor.copy(patch.hoverColor);
        scene.forceRender = true;
    });

    // ── list zone ids ─────────────────────────────────────────────────────────
    events.function('clickableZones.list', () => [...zones.keys()]);

    // ── toggle all zones visible/hidden ───────────────────────────────────────
    let zonesVisible = true;
    events.function('clickableZones.toggleVisible', () => {
        zonesVisible = !zonesVisible;
        for (const rec of zones.values()) {
            rec.options.visible = zonesVisible;
        }
        if (!zonesVisible) {
            tooltipEl.style.display = 'none';
            canvas.style.cursor = '';
        }
        scene.forceRender = true;
        events.fire('clickableZones.visibilityChanged', zonesVisible);
        return zonesVisible;
    });

    // ── list all zones with full data ─────────────────────────────────────────
    events.function('clickableZones.listAll', () => {
        return [...zones.values()].map(rec => ({
            id: rec.id,
            label: rec.options.label,
            supportingImages: rec.options.supportingImages,
            position: rec.options.position.clone(),
            halfExtents: rec.options.halfExtents.clone(),
        }));
    });

    events.on('clickableZones.rename', ({ id, label }: { id: string, label: string }) => {
        const rec = zones.get(id);
        if (!rec) return;
        rec.options.label = label;
    });

    events.on('clickableZones.addImages', ({ id, imageNames }: { id: string, imageNames: string[] }) => {
        const rec = zones.get(id);
        if (!rec) return;
        for (const name of imageNames) {
            if (!rec.options.supportingImages.includes(name)) {
                rec.options.supportingImages.push(name);
            }
        }
    });

    events.on('clickableZones.removeImage', ({ id, imageName }: { id: string; imageName: string }) => {
        const rec = zones.get(id);
        if (!rec) return;
        rec.options.supportingImages = rec.options.supportingImages.filter(n => n !== imageName);
    });

    events.on('clickableZones.focusOn', (id: string) => {
        const rec = zones.get(id);
        if (!rec) return;
        events.fire('camera.focusOnPoint', {
            position: rec.options.position.clone(),
            radius: rec.options.halfExtents.length(),
        });
    });

    // ── hover detection ───────────────────────────────────────────────────────
    function updateHover(screenX: number, screenY: number) {
        if (zones.size === 0) return;
        const cam = scene.camera.entity.camera;
        const origin = new Vec3(), farPt = new Vec3();
        cam.screenToWorld(screenX, screenY, 0.01, origin);
        cam.screenToWorld(screenX, screenY, 1000, farPt);
        const dir = farPt.clone().sub(origin).normalize();
        ray.set(origin, dir);

        let newHovered: string | null = null, bestDist = Infinity;
        for (const [id, rec] of zones) {
            if (!rec.options.visible) continue;
            if (rayIntersectsAabb(ray, rec.aabb)) {
                const d = origin.distance(rec.aabb.center);
                if (d < bestDist) { bestDist = d; newHovered = id; }
            }
        }

        if (newHovered !== hoveredId) {
            if (hoveredId) { const old = zones.get(hoveredId); if (old) old.hovered = false; }
            hoveredId = newHovered;
            if (hoveredId) {
                const rec = zones.get(hoveredId);
                if (rec) rec.hovered = true;
                canvas.style.cursor = 'pointer';
                const label = zones.get(hoveredId)?.options.label ?? '';
                if (label) { tooltipEl.textContent = label; tooltipEl.style.display = 'block'; }
            } else {
                canvas.style.cursor = '';
                tooltipEl.style.display = 'none';
            }
            events.fire('clickableZone.hovered', hoveredId);
            scene.forceRender = true;
        }
    }

    // ── draw all zones onto 2D overlay ────────────────────────────────────────
    function renderOverlay() {
        ctx2d.clearRect(0, 0, overlay.width, overlay.height);
        if (zones.size === 0) return;

        const camEntity = scene.camera.entity;
        const camPos = camEntity.getPosition();
        const camFwd = camEntity.forward;
        const near = camEntity.camera.nearClip;
        const wts = (w: Vec3, s: Vec3) => camEntity.camera.worldToScreen(w, s);

        for (const [, rec] of zones) {
            if (!rec.options.visible) continue;
            const col = rec.hovered ? rec.options.hoverColor : rec.options.color;
            const r = Math.round(col.r * 255);
            const g = Math.round(col.g * 255);
            const b = Math.round(col.b * 255);

            setCorners(rec.options.position, rec.options.halfExtents);

            if (rec.hovered) {
                ctx2d.globalAlpha = 1.0;
                ctx2d.fillStyle = `rgba(${r},${g},${b},0.18)`;
                drawHoverFill(ctx2d, camPos, camFwd, near, wts);
            }

            ctx2d.beginPath();
            ctx2d.strokeStyle = `rgb(${r},${g},${b})`;
            ctx2d.lineWidth = rec.hovered ? 2.5 : 1.5;
            ctx2d.globalAlpha = rec.hovered ? 1.0 : 0.75;

            for (const [a, b_] of EDGES) {
                strokeEdge(ctx2d, _c[a], _c[b_], camPos, camFwd, near, wts);
            }
            ctx2d.stroke();
        }
        ctx2d.globalAlpha = 1.0;
    }

    // ── pointer events ────────────────────────────────────────────────────────
    const onPointerMove = (e: PointerEvent) => {
        mouseX = e.offsetX; mouseY = e.offsetY;
        updateHover(mouseX, mouseY);
        if (hoveredId && tooltipEl.style.display !== 'none') {
            tooltipEl.style.left = `${e.clientX}px`;
            tooltipEl.style.top = `${e.clientY}px`;
        }
    };

    const onPointerDown = (e: PointerEvent) => {
        if (e.button !== 0) return;
        if (hoveredId) events.fire('clickableZone.clicked', hoveredId);
    };

    const onPointerLeave = () => {
        mouseX = -1; mouseY = -1;
        if (hoveredId) {
            const rec = zones.get(hoveredId);
            if (rec) rec.hovered = false;
            hoveredId = null;
            canvas.style.cursor = '';
            tooltipEl.style.display = 'none';
            events.fire('clickableZone.hovered', null);
            scene.forceRender = true;
        }
    };

    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointerleave', onPointerLeave);

    // ── prerender: update hover + redraw overlay ──────────────────────────────
    events.on('prerender', () => {
        if (mouseX >= 0 && zones.size > 0) updateHover(mouseX, mouseY);
        renderOverlay();
        if (zones.size > 0) scene.forceRender = true;
    });

    // ── cleanup ───────────────────────────────────────────────────────────────
    return () => {
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointerleave', onPointerLeave);
        window.removeEventListener('resize', syncOverlay);
        overlay.remove();
        tooltipEl.remove();
        zones.clear();
    };
}
